import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { tokenStore } from '../lib/tokenStore';

interface User {
  id: string;
  email: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  role: string;
  agencyId?: number | null;
  verified?: boolean;
  profilePicture?: string;
  avatarColor?: string;
}

interface AuthContextType {
  user: User | null;
  role: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string) => Promise<User | null>;
  logout: () => void;
  refreshUser: () => Promise<User | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // On mount: if we have an in-memory token (SPA navigation) use it,
    // otherwise try to restore session via the refresh token in sessionStorage.
    if (tokenStore.getAccess()) {
      loadUser();
    } else if (tokenStore.getRefresh()) {
      tryRefreshAndLoad();
    } else {
      setIsLoading(false);
    }
  }, []);

  const tryRefreshAndLoad = async (): Promise<void> => {
    const refreshToken = tokenStore.getRefresh();
    if (!refreshToken) { setIsLoading(false); return; }
    try {
      const baseURL = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8787/api';
      const res = await fetch(`${baseURL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.accessToken) {
          tokenStore.setAccess(data.accessToken);
          if (data.refreshToken) tokenStore.setRefresh(data.refreshToken);
          await loadUser();
          return;
        }
      }
    } catch {
      // silent — fall through to clear
    }
    tokenStore.clear();
    setIsLoading(false);
  };

  const loadUser = async (token?: string): Promise<User | null> => {
    try {
      // Use centralized apiClient so refresh flow and API base are handled consistently
      const apiModule = await import('../api/apiClient')
      const apiClient = apiModule.default

      // If a token was explicitly provided (e.g. right after login), put it in the store first
      if (token) tokenStore.setAccess(token);

      try {
        const userData = await apiClient.request<User>('/auth/me')
        setUser(userData)
        return userData
      } catch (e: any) {
        // apiClient will attempt refresh if possible; if it fails, clear tokens and return null
        console.warn('/auth/me via apiClient failed', e?.message || e)
        tokenStore.clear()
        setUser(null)
        return null
      }
    } catch (error) {
      console.error('Failed to load user:', error);
      tokenStore.clear();
      setUser(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  };


  const login = async (token: string): Promise<User | null> => {
    try {
      // Note: Login.tsx should have already stored both tokens
      // But use the provided token directly to avoid race with localStorage
      const u = await loadUser(token);
      return u;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = () => {
    tokenStore.clear();
    setUser(null);
    window.location.href = '/login';
  };

  const refreshUser = async (): Promise<User | null> => {
    return await loadUser();
  };

  const value: AuthContextType = {
    user,
    role: user?.role || null,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const SAFE_DEFAULT: AuthContextType = {
  user: null,
  role: null,
  isAuthenticated: false,
  isLoading: true,
  login: async () => null,
  logout: () => {},
  refreshUser: async () => null,
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    // During dev/HMR it's possible components render before the provider is wired up.
    // Return a safe default to avoid hard crashes; log a warning so we can detect mis-wiring.
    // Applications should still wrap the tree with <AuthProvider>.
    // eslint-disable-next-line no-console
    console.warn('useAuth called outside AuthProvider — returning safe fallback');
    return SAFE_DEFAULT;
  }
  return context;
};
